import React, { useEffect, useRef, useState } from 'react'
import { Link, NavLink, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import TranslationNS from '@/utils/Constants/TranslationNS'
import ArrowSVG from '@/images/svgs/Arrow.svg?react'
import { useMin } from '@/hooks/useBreakpoint'
import { useEventHub } from './EventHubContext'
import EventHubLabeledDropdown from './EventHubLabeledDropdown'
import { EVENT_HUB_TABS } from './tabs.config'
import { getEventHubBase } from './eventHub.utils'

/** Desktop: the tab bar sticks this far from the viewport top (mock `.section-tabs{top:12px}`). */
const STICKY_TOP = 12
/** Gap kept between the stuck bar and whatever sticks under it (leaderboard list header). */
const STICKY_GAP = 10
/** CSS variable read by the leaderboard list header to stick right under the tab bar. */
export const TAB_BAR_STUCK_TOP_VAR = '--lh-top'

/**
 * URL-driven tab subnavigation. Links are absolute and preserve the /sporthub
 * prefix so deep-linking + refresh land on the right tab. Active state comes
 * from NavLink. Rendered once by EventHub.jsx.
 *
 * The tab row scrolls horizontally with the scrollbar hidden and a white fade on
 * whichever edge still has content (same treatment as the home SearchBar). On
 * mobile the row is replaced by a sticky top "logo + <tab>" switcher on every tab
 * (mock `.section-tabs` "option D", Figma 3790:33277); the bottom of the screen is
 * left to the leaderboard's docked controls / the registration bar.
 * `fullWidth` (set by EventHub for tabs without a side panel, e.g. the leaderboard)
 * drops the right spacer column on desktop.
 *
 * Once the bar is actually pinned, a white curtain hides the page scrolling through
 * the gap above it, and the event logo slides in at the left of the tabs / dropdown
 * (mock `.section-tabs.stuck` / `.event-badge`). Its height is published as
 * `--lh-top` so the leaderboard header can stick under it (desktop and mobile).
 */
export default function EventHubTabNav({ fullWidth = false, stickyRef }) {
    const { t } = useTranslation(TranslationNS.EVENT_PAGE)
    const { eventIdentifier } = useParams()
    const { pathname } = useLocation()
    const navigate = useNavigate()
    const { competition } = useEventHub()
    const isDesktop = useMin('nav')

    const base = getEventHubBase(pathname, eventIdentifier)
    const tabHref = (tab) => (tab.path ? `${base}/${tab.path}` : base)
    const activeTab =
        EVENT_HUB_TABS.find((tab) => tab.path && pathname.replace(/\/+$/, '').endsWith(`/${tab.path}`)) ||
        EVENT_HUB_TABS[0]

    const wrapperRef = useRef(null)
    const rowRef = useRef(null)

    // The sticky box the parent scrolls under on a screen change (useScreenTop): the padded wrapper on
    // both breakpoints (its computed `top` is the stuck offset: 12 on desktop, 0 on mobile).
    useEffect(() => {
        if (!stickyRef) return undefined
        stickyRef.current = wrapperRef.current
        return () => {
            if (stickyRef.current === wrapperRef.current) stickyRef.current = null
        }
    }, [stickyRef])
    const navRef = useRef(null)
    const scrollRef = useRef(null)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)
    const [stuck, setStuck] = useState(false)

    useEffect(() => {
        const el = scrollRef.current
        if (!el) return undefined

        const update = () => {
            const maxScrollLeft = el.scrollWidth - el.clientWidth
            setCanScrollLeft(el.scrollLeft > 1)
            setCanScrollRight(el.scrollLeft < maxScrollLeft - 1)
        }
        update()

        el.addEventListener('scroll', update)
        window.addEventListener('resize', update)
        return () => {
            el.removeEventListener('scroll', update)
            window.removeEventListener('resize', update)
        }
    }, [])

    // Stuck detection: the bar is really pinned — not merely at the top of the page, nor
    // being pushed out by the end of its container. Desktop: the row sits at STICKY_TOP;
    // mobile: the padded wrapper itself sticks at 0.
    useEffect(() => {
        const update = () => {
            const el = isDesktop ? rowRef.current : wrapperRef.current
            if (!el) return
            const top = el.getBoundingClientRect().top
            setStuck(window.scrollY > 0 && Math.abs(top - (isDesktop ? STICKY_TOP : 0)) < 0.5)
        }
        update()
        // capture: the scroll may come from a nested scroll container
        document.addEventListener('scroll', update, { passive: true, capture: true })
        window.addEventListener('resize', update)
        return () => {
            document.removeEventListener('scroll', update, { capture: true })
            window.removeEventListener('resize', update)
        }
    }, [isDesktop])

    // Publish the pinned box's bottom edge (+ gap) so the leaderboard header sticks under it.
    // Desktop: the bar pinned at STICKY_TOP (+ gap, page is white there); mobile: the padded
    // wrapper pinned at 0, no gap — the compact list header carries it as white padding.
    useEffect(() => {
        const el = isDesktop ? navRef.current : wrapperRef.current
        const offset = isDesktop ? STICKY_TOP + STICKY_GAP : 0
        const root = document.documentElement
        if (!el || typeof ResizeObserver === 'undefined') {
            root.style.removeProperty(TAB_BAR_STUCK_TOP_VAR)
            return undefined
        }
        const observer = new ResizeObserver((entries) => {
            const height = entries[0]?.borderBoxSize?.[0]?.blockSize || el.offsetHeight
            root.style.setProperty(TAB_BAR_STUCK_TOP_VAR, `${Math.round(height) + offset}px`)
        })
        observer.observe(el)
        return () => {
            observer.disconnect()
            root.style.removeProperty(TAB_BAR_STUCK_TOP_VAR)
        }
    }, [isDesktop])

    // Centre the active tab in the strip when the strip overflows (no-op otherwise).
    useEffect(() => {
        const box = scrollRef.current
        const el = box?.querySelector('[aria-current="page"]')
        if (!box || !el || box.scrollWidth <= box.clientWidth + 1) return
        const b = box.getBoundingClientRect()
        const e = el.getBoundingClientRect()
        box.scrollLeft = Math.max(0, box.scrollLeft + (e.left - b.left) - (b.width - e.width) / 2)
    }, [pathname])

    // Scroll ~60% of the visible width per arrow click; the scroll listener above
    // keeps canScrollLeft/Right (and thus the arrows) in sync afterwards.
    const scrollByStep = (direction) => {
        const el = scrollRef.current
        if (!el) return
        el.scrollBy({ left: direction * el.clientWidth * 0.6, behavior: 'smooth' })
    }

    return (
        <div
            ref={wrapperRef}
            // This wrapper is the sticky box (its parent is the whole hub, so it can travel
            // the full page; the row alone was capped by this wrapper's own height).
            // Desktop: vertical spacing is margin, not padding, so the pinned box is just the bar.
            // Mock `.shell{gap:16px}` / `.ev-main{gap:14px}`: 16px under the banner, 14px above the content.
            // Mobile: sticks at 0 with its 18px padding; once pinned the wrapper turns white so
            // the card scrolling under it never shows through the padding (mock curtain).
            className={[
                'pt-(--x001) nav:sticky nav:top-3 nav:z-20 nav:mt-4 nav:mb-3.5 nav:px-(--x006) nav:py-0',
                'max-nav:sticky max-nav:top-0 max-nav:z-20 max-nav:px-(--x004) max-nav:pt-(--x004) max-nav:pb-0',
                stuck ? 'max-nav:bg-white' : '',
            ].join(' ')}
        >
            {/* Bar row. When pinned, a white curtain (::before) covers the full content width
                plus the 12px above so nothing scrolls through the gap. */}
            <div
                ref={rowRef}
                className={[
                    // 70px = the mock `.ev-cols` gutter between the content column and the aside
                    'flex gap-[70px] nav:relative',
                    stuck
                        ? "nav:before:pointer-events-none nav:before:absolute nav:before:-inset-x-(--x006) nav:before:-top-3 nav:before:bottom-0 nav:before:-z-10 nav:before:bg-white nav:before:content-['']"
                        : '',
                ].join(' ')}
            >
                {/* Mobile section switcher: event logo + "<tab>" dropdown. The logo slides in
                    (0 → 46px) only once the card is pinned: at scroll top the hero header right
                    above already shows it. The negative margin cancels the card gap while hidden. */}
                {!isDesktop && (
                    <div
                        className="flex w-full items-center gap-2.5 rounded-[10px] border border-[#DBDADF] bg-white p-2.5"
                        data-tour="hub-tabs"
                    >
                        {competition?.iconLink && (
                            <div
                                aria-hidden={!stuck}
                                className={[
                                    'h-[46px] flex-none overflow-hidden rounded-[8px] border-[#dbdadf] bg-white transition-[width,opacity,margin] duration-200 ease-out motion-reduce:transition-none',
                                    stuck ? 'w-[46px] border opacity-100' : 'w-0 border-0 opacity-0 -mr-2.5',
                                ].join(' ')}
                            >
                                <img src={competition.iconLink} alt="" className="block size-full object-cover" />
                            </div>
                        )}
                        <EventHubLabeledDropdown
                            ariaLabel={t('Section')}
                            options={EVENT_HUB_TABS.map((tab) => ({ value: tab.key, label: t(tab.labelKey) }))}
                            value={activeTab.key}
                            onChange={(key) => {
                                const tab = EVENT_HUB_TABS.find((item) => item.key === key)
                                if (tab) navigate(tabHref(tab))
                            }}
                            size="lg"
                            wrapperClassName="min-w-0 flex-1"
                        />
                    </div>
                )}

                <nav
                    ref={navRef}
                    data-tour="hub-tabs"
                    className={[
                        // desktop only: mobile uses the section switcher above
                        'relative hidden w-full items-center gap-2.5 overflow-hidden rounded-[10px] border border-[#DBDADF] bg-white p-3.75 nav:flex',
                        // white fade on whichever edge still has content to scroll (matches the SearchBar)
                        canScrollLeft
                            ? `before:pointer-events-none before:absolute before:inset-y-0 before:z-10 before:w-13.5 before:bg-linear-to-r before:from-white before:to-white/0 before:content-[''] ${
                                  stuck ? 'before:left-[47px]' : 'before:left-0'
                              }`
                            : '',
                        canScrollRight
                            ? "after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:z-10 after:w-13.5 after:bg-linear-to-l after:from-white after:to-white/0 after:content-['']"
                            : '',
                    ].join(' ')}
                >
                    {/* Event badge: 0 → 37px wide once the bar is pinned; the negative margin
                        cancels the row gap while hidden so the tabs stay centred (desktop only). */}
                    {competition?.iconLink && (
                        <Link
                            to={base}
                            aria-label={competition.name}
                            aria-hidden={!stuck}
                            tabIndex={stuck ? 0 : -1}
                            className={[
                                'relative hidden h-[37px] flex-none overflow-hidden rounded-[5px] border-[#dbdadf] transition-[width,opacity,margin] duration-200 ease-out hover:border-[#272727] motion-reduce:transition-none nav:block',
                                stuck ? 'w-[37px] border opacity-100' : 'w-0 border-0 opacity-0 -mr-2.5',
                            ].join(' ')}
                        >
                            <img src={competition.iconLink} alt="" className="block size-full object-cover" />
                        </Link>
                    )}

                    <div
                        ref={scrollRef}
                        className="flex min-w-0 flex-1 gap-2.5 overflow-x-auto nav:justify-center-safe [&::-webkit-scrollbar]:hidden [scrollbar-width:none] [-ms-overflow-style:none]"
                    >
                        {EVENT_HUB_TABS.map((tab) => (
                            <NavLink
                                key={tab.key}
                                to={tabHref(tab)}
                                end={tab.path === ''}
                                className={({ isActive }) =>
                                    [
                                        // mock `.section-tabs a[role=tab]`: 14px on a 20px line, padding 10, radius 5,
                                        // muted ink; active = field background + Medium + strong ink; hover = field
                                        'shrink-0 whitespace-nowrap rounded-[5px] p-2.5 leading-5 text-black/50 transition-colors hover:bg-[#f7f7f9] hover:text-black/50',
                                        isActive
                                            ? 'bg-[#f7f7f9] font-medium text-[#272727]! hover:text-[#272727]!'
                                            : '',
                                    ].join(' ')
                                }
                            >
                                {t(tab.labelKey)}
                            </NavLink>
                        ))}
                    </div>

                    {/* Desktop-only scroll affordances. Each arrow appears the moment its edge can
                        scroll, points toward the scroll direction (the SVG points up: clockwise
                        rotate-90 = right, -rotate-90 = left), and carries a soft white halo
                        (box-shadow blur + spread) that blends it into the white bar over the tabs. */}
                    {canScrollLeft && (
                        <button
                            type="button"
                            aria-label="Scroll tabs left"
                            onClick={() => scrollByStep(-1)}
                            className={`absolute top-1/2 z-20 hidden -translate-y-1/2 cursor-pointer border-0 bg-transparent p-0 nav:flex ${
                                stuck ? 'left-[51px]' : 'left-1'
                            }`}
                        >
                            <ArrowSVG className="size-[18px] text-[#272727] overflow-visible box-content -rotate-90 rounded-full bg-white p-1.5 shadow-[0_0_18px_14px_#fff]" />
                        </button>
                    )}
                    {canScrollRight && (
                        <button
                            type="button"
                            aria-label="Scroll tabs right"
                            onClick={() => scrollByStep(1)}
                            className="absolute right-1 top-1/2 z-20 hidden -translate-y-1/2 cursor-pointer border-0 bg-transparent p-0 nav:flex"
                        >
                            <ArrowSVG className="size-[18px] text-[#272727] overflow-visible box-content rotate-90 rounded-full bg-white p-1.5 shadow-[0_0_18px_14px_#fff]" />
                        </button>
                    )}
                </nav>

                {/* Reserves the right column (≥ cols, where the side panel floats beside the content) so it
                    never hides the tab bar. Width tracks --eh-sidebar-w — the same var that sizes the panel's grid column. */}
                {!fullWidth && <div aria-hidden className="hidden shrink-0 cols:block cols:w-(--eh-sidebar-w)" />}
            </div>
        </div>
    )
}
